import {
  Box,
  Flex,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  Image,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  Badge,
  HStack,
  Img,
} from "@chakra-ui/react";
import React, { memo, useMemo } from "react";
import IconX from "../../../assets/images/booking/x-circle.svg";
import chevronLeft from "../../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../../assets/images/employee/chevron-right.svg";
import ReactPaginate from "react-paginate";
import { useBrokerDebtCertificatePeriod } from "../../../hooks/useBrokerDebtCertificatePeriod";

const COLS = [
  { label: "STT", key: "__stt", w: "5%", align: "center" },
  { label: "Mã căn", key: "apartmentCode", w: "12%", align: "start" },
  { label: "Đợt", key: "period", w: "10%", align: "center" },
  { label: "Tháng/Năm", key: "monthYear", w: "12%", align: "center" },
  { label: "Số tiền CCMG", key: "amountHeld", w: "15%", align: "end" },
  { label: "Ngày cập nhật", key: "updatedDate", w: "14%", align: "center" },
  { label: "Nguồn dữ liệu", key: "source", w: "12%", align: "center" },
];

const FormHistoryInfo = memo(({ isOpen, onClose, defaultValues }) => {
  const employeeId = useMemo(
    () => defaultValues?.employeeId || null,
    [defaultValues],
  );

  const {
    periodListData,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useBrokerDebtCertificatePeriod(employeeId);

  const handlePageChange = ({ selected }) => setCurrentPage(selected + 1);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        position={"relative"}
        w={"100%"}
        maxW={"60%"}
      >
        <Box
          paddingBottom={"80px"}
          h={"86.5vh"}
          w={"100%"}
          overflowY={"auto"}
          sx={{
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              width: "4px",
              borderRadius: "50px",
              background: "var(--color-background)",
            },
          }}
        >
          {/* Header */}
          <Flex
            alignItems={"center"}
            p={"25px 24px 16px 24px"}
            position={"relative"}
            borderBottom={"1px solid var(--border-input-employee)"}
          >
            <Box>
              <Text
                color={"var(--color-header-employee)"}
                fontSize={"16px"}
                fontWeight={"700"}
              >
                Lịch sử tạm giữ CCMG
              </Text>
              <Text fontSize="12px" color="#51607B" mt="4px">
                {defaultValues?.nameEmployee || ""}{" "}
                {defaultValues?.codeEmployee
                  ? `(${defaultValues?.codeEmployee})`
                  : ""}
              </Text>
            </Box>
            <Box
              position={"absolute"}
              top={"18px"}
              right={"18px"}
              cursor={"pointer"}
              onClick={onClose}
            >
              <Image src={IconX} />
            </Box>
          </Flex>

          {/* Table */}
          <Box p={"24px"}>
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              <TableContainer
                w={"100%"}
                maxH="calc(100vh - 330px)"
                overflowY="auto"
              >
                <Table w={"100%"} variant="unstyled">
                  <Thead
                    position="sticky"
                    top="0"
                    zIndex="1"
                    w={"100%"}
                    h={"41px"}
                    backgroundColor="#F9FAFD"
                  >
                    <Tr h={"41px"}>
                      {COLS.map((col, i) => (
                        <Td
                          key={i}
                          p={"16px 16px 8px 16px"}
                          border={"none"}
                          color={"#51607B"}
                          fontSize={"14px"}
                          fontWeight={400}
                          w={col.w}
                          textAlign={col.align}
                        >
                          {col.label}
                        </Td>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!periodListData || periodListData.length === 0 ? (
                      <Tr>
                        <Td
                          colSpan={COLS.length}
                          textAlign="center"
                          py="32px"
                          color="#51607B"
                          fontSize="14px"
                        >
                          Chưa có dữ liệu lịch sử CCMG
                        </Td>
                      </Tr>
                    ) : (
                      periodListData.map((row, idx) => (
                        <Tr
                          key={row._id || idx}
                          h={"44px"}
                          borderBottom={
                            "1px solid var(--border-input-employee)"
                          }
                          _hover={{ bg: "var(--color-hover-row)" }}
                        >
                          <Td
                            p={"8px 16px"}
                            textAlign="center"
                            fontSize={"14px"}
                            color={"#1A2A44"}
                          >
                            {(currentPage - 1) * 20 + idx + 1}
                          </Td>
                          <Td
                            p={"8px 16px"}
                            fontSize={"14px"}
                            color={"#1A2A44"}
                            fontWeight={500}
                          >
                            {row.apartmentCode}
                          </Td>
                          <Td
                            p={"8px 16px"}
                            textAlign="center"
                            fontSize={"14px"}
                            color={"#1A2A44"}
                          >
                            {row.period}
                          </Td>
                          <Td
                            p={"8px 16px"}
                            textAlign="center"
                            fontSize={"14px"}
                            color={"#1A2A44"}
                          >
                            {row.monthYear}
                          </Td>
                          <Td
                            p={"8px 16px"}
                            textAlign="end"
                            fontSize={"14px"}
                            color={"#1A2A44"}
                            fontWeight={500}
                          >
                            {row.amountHeld}
                          </Td>
                          <Td
                            p={"8px 16px"}
                            textAlign="center"
                            fontSize={"14px"}
                            color={"#1A2A44"}
                          >
                            {row.updatedDate}
                          </Td>
                          <Td p={"8px 16px"} textAlign="center">
                            <Badge
                              colorScheme={
                                row.source === "PDV" ? "blue" : "orange"
                              }
                              fontSize={"12px"}
                              px={"8px"}
                              py={"2px"}
                              borderRadius={"6px"}
                            >
                              {row.source}
                            </Badge>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Skeleton>

            {/* Pagination */}
            {totalPages > 1 && <Box mt={6}>
              <Flex alignItems={"center"} justifyContent={"space-between"}>
                <Text>
                 {startEntry} - {endEntry} / {entries} bản ghi
                </Text>
                <HStack spacing={2} justify="flex-end">
                  <ReactPaginate
                    className="pagination-custom"
                    pageCount={totalPages}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    onPageChange={handlePageChange}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                    breakClassName={"ellipsis"}
                    breakLabel={"..."}
                    previousLabel={
                      <Img
                        w={"16px"}
                        minW={"16px"}
                        h={"16px"}
                        src={chevronLeft}
                      />
                    }
                    nextLabel={
                      <Img
                        w={"16px"}
                        minW={"16px"}
                        h={"16px"}
                        src={chevronRight}
                      />
                    }
                    renderOnZeroPageCount={null}
                    forcePage={currentPage - 1}
                  />
                </HStack>
              </Flex>
            </Box>}
          </Box>
        </Box>
      </ModalContent>
    </Modal>
  );
});

export default FormHistoryInfo;
